import React from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  SheetClose,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { ShoppingCart, Plus, Minus, Trash2 } from 'lucide-react';
import { currencyFmt, placeholderImg, OTHER_RATE } from '@/utils/ui';
import { useTranslation } from 'react-i18next';

const CartDrawer = ({
  open,
  onOpenChange,
  cart = [],
  currency = 'USD',
  onIncQty,
  onDecQty,
  onSetQty,
  onRemove,
  onCheckout,
  checkoutLoading = false,
}) => {
  const { t } = useTranslation();

  const cartCount = cart.reduce(
    (n, it) => n + Number(it.quantity || 0),
    0
  );
  const subtotal = cart.reduce(
    (sum, it) =>
      sum +
      Number(it.product.price || 0) *
        Number(it.quantity || 0),
    0
  );
  const fee = subtotal * OTHER_RATE; // single combined fee
  const total = subtotal + fee;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <ShoppingCart size={18} />
            {t('groupSpace.cartDrawer.title')}
            {cartCount > 0 && (
              <span className="ml-2 text-xs rounded-full bg-slate-200 px-2 py-0.5">
                {cartCount}
              </span>
            )}
          </SheetTitle>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          {cart.length === 0 ? (
            <div className="text-center text-slate-500 py-16">
              {t('groupSpace.cartDrawer.empty')}
            </div>
          ) : (
            <>
              <div className="space-y-3">
                {cart.map((it) => {
                  const remaining =
                    it.product.stock != null
                      ? Number(it.product.stock) -
                        Number(it.quantity)
                      : null;
                  return (
                    <div
                      key={it.product.id}
                      className="flex items-start gap-3 border rounded-lg p-3"
                    >
                      <img
                        src={it.product.image || placeholderImg}
                        alt={it.product.name}
                        className="w-16 h-16 object-cover rounded-md"
                        onError={(e) => {
                          const img = e.target;
                          img.src = placeholderImg;
                        }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="font-medium truncate">
                              {it.product.name}
                            </p>
                            <p className="text-sm text-slate-600">
                              {currencyFmt(
                                it.product.price,
                                it.product.currency ||
                                  currency
                              )}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() =>
                              onRemove(it.product.id)
                            }
                            aria-label={t(
                              'groupSpace.cartDrawer.remove'
                            )}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>

                        <div className="mt-2 flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() =>
                              onDecQty(it.product.id)
                            }
                          >
                            <Minus size={14} />
                          </Button>
                          <input
                            className="w-14 h-9 border rounded-md text-center"
                            type="number"
                            min={1}
                            value={it.quantity}
                            onChange={(e) =>
                              onSetQty(
                                it.product.id,
                                e.target.value
                              )
                            }
                          />
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() =>
                              onIncQty(it.product.id)
                            }
                            disabled={
                              it.product.stock != null &&
                              Number(it.quantity) >=
                                Number(it.product.stock)
                            }
                          >
                            <Plus size={14} />
                          </Button>
                          {remaining != null && (
                            <span className="ml-2 text-xs text-slate-500">
                              {t(
                                'groupSpace.cartDrawer.available',
                                { count: remaining }
                              )}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-4 border rounded-lg p-4">
                <div className="space-y-1 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">
                      {t('groupSpace.cartDrawer.subtotal')}
                    </span>
                    <span className="font-medium">
                      {currencyFmt(subtotal, currency)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">
                      {t('groupSpace.cartDrawer.taxes')}
                    </span>
                    <span className="font-medium">
                      {currencyFmt(fee, currency)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t mt-2">
                    <span className="text-slate-700 font-semibold">
                      {t('groupSpace.cartDrawer.total')}
                    </span>
                    <span className="text-lg font-semibold">
                      {currencyFmt(total, currency)}
                    </span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <SheetFooter className="mt-4">
          <div className="flex w-full gap-2">
            <SheetClose asChild>
              <Button className="flex-1" variant="outline">
                {t('groupSpace.cartDrawer.close')}
              </Button>
            </SheetClose>
            <Button
              className="flex-1"
              onClick={onCheckout}
              disabled={!cart.length || checkoutLoading}
            >
              {checkoutLoading
                ? t('groupSpace.cartDrawer.startingCheckout')
                : t('groupSpace.cartDrawer.buyEntireCart')}
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
